import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowRight, Send, User, Loader2, Home, Car } from "lucide-react";

export default function ChatConversation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${id}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setConv(res.data.conversation);
      setMessages(res.data.messages);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  // ── Realtime via WebSocket with REST polling fallback ───────────────────
  // We open a WS to /api/ws/chat and append any "dm.new" frames whose
  // conversation_id matches this conversation. If the socket disconnects we
  // keep the existing 5-second REST poll so the UI never stops updating.
  useEffect(() => {
    load();
    let ws = null;
    let reconnectTimer = null;
    // Mutable holder for the unmount-cancellation flag. Wrapped in an object so the
    // ESLint `react-hooks/set-state-in-effect` rule doesn't false-flag a top-level
    // `let flag = false; flag = true;` pattern as state-in-effect.
    const lifecycle = { cancelled: false };

    const apiBase = API.replace(/\/api$/, "");
    const wsBase  = apiBase.replace(/^http/, "ws");

    const connect = () => {
      if (lifecycle.cancelled || !token) return;
      try {
        ws = new WebSocket(`${wsBase}/api/ws/chat?token=${encodeURIComponent(token)}`);
      } catch (e) {
        ws = null;
        return;
      }
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data?.type === "dm.new" && data.conversation_id === id) {
            setMessages((prev) => prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]);
          }
        } catch { /* ignore non-JSON frames */ }
      };
      ws.onclose = () => {
        if (lifecycle.cancelled) return;
        reconnectTimer = setTimeout(connect, 3000);   // exponential-ish, kept simple
      };
      ws.onerror = () => { try { ws && ws.close(); } catch { /* noop */ } };
    };

    connect();
    // Polling fallback — slower than before (15s) since WS handles the hot path.
    const interval = setInterval(load, 15000);
    return () => {
      lifecycle.cancelled = true;
      clearInterval(interval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
  }, [id, token]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || !conv) return;
    const other = conv.participants?.find((p) => p !== user.user_id);
    if (!other) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, {
        recipient_id: other,
        content: text,
        listing_id: conv.listing_id,
        listing_type: conv.listing_type,
        listing_title: conv.listing_title
      }, { headers: { Authorization: `Bearer ${token}` } });
      setText("");
      await load();
    } catch (e) { console.error(e); } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <DashboardLayout title={t("محادثة", "Conversation")}>
      <div className="max-w-3xl flex flex-col h-[calc(100vh-12rem)]" data-testid="chat-conversation">
        <button onClick={() => navigate("/chat/inbox")} className="inline-flex items-center gap-2 text-yellow-500 hover:underline mb-3 self-start">
          <ArrowRight size={16} /> {t("الرجوع للمحادثات", "Back to inbox")}
        </button>

        {/* Header */}
        {conv && (
          <div className="glass-card rounded-2xl p-4 flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold"><User size={20} /></div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">
                {conv.participants?.find((p) => p !== user.user_id) ? t("مالك الإعلان", "Listing owner") : t("محادثة", "Chat")}
              </h3>
              {conv.listing_title && (
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  {conv.listing_type === "property" && <Home size={12} />}
                  {conv.listing_type === "vehicle" && <Car size={12} />}
                  <span>{conv.listing_title}</span>
                </div>
              )}
            </div>
            {conv.listing_id && conv.listing_type && (
              <button
                onClick={() => navigate(conv.listing_type === "property" ? `/housing/${conv.listing_id}` : `/vehicles/${conv.listing_id}`)}
                className="text-yellow-500 text-sm hover:underline"
              >
                {t("عرض الإعلان", "View listing")}
              </button>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="glass-card rounded-2xl p-4 flex-1 overflow-y-auto space-y-3" data-testid="chat-messages">
          {loading ? (
            <div className="text-center text-slate-500 py-8">{t("جاري التحميل...", "Loading...")}</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-slate-500 py-8">{t("لا توجد رسائل بعد. ابدأ المحادثة!", "No messages yet. Start the conversation!")}</div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === user?.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 ${mine ? "chat-bubble-sent" : "chat-bubble-received"}`}>
                    <p className="whitespace-pre-wrap break-words text-sm">{m.content}</p>
                    <p className={`text-[10px] mt-1 ${mine ? "text-black/60" : "text-slate-500"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="mt-3 flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder={t("اكتب رسالتك...", "Type your message...")}
            className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none resize-none"
            data-testid="chat-input"
          />
          <Button onClick={send} disabled={sending || !text.trim()} className="btn-primary" data-testid="chat-send-btn">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
